import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, CardHeader, CardBody, Form, Label, InputGroup, Input, Table } from 'reactstrap';
import moment from 'moment';
import DOMPurify from 'dompurify';
import { Insurer, getInsurers } from 'features/insurers/insurersAPI';
import { Aranceles as ArancelesType, Pacto, getAranceles, getPactos } from 'features/aranceles/arancelesAPI';

export default function Aranceles() {
  const [isLoading, setIsLoading] = useState(false);
  const [insurers, setInsurers] = useState<Insurer[]>([]);
  const [selectedInsurer, setSelectedInsurer] = useState(-1);
  const [pactos, setPactos] = useState<Pacto[]>([]);
  const [selectedPacto, setSelectedPacto] = useState<number>(-1);
  const [aranceles, setAranceles] = useState<ArancelesType>();
  const [errorMessage, setErrorMessage] = useState<string>('');

  const selectRef = useRef<HTMLInputElement>(null);

  const isVigente = (p: Pacto): boolean => {
    const today = moment().startOf('day');
    const inicio = moment(p.fechaInicioVigencia, 'YYYY-MM-DD').startOf('day');
    if (inicio.isAfter(today)) return false;
    if (!p.fechaFinVigencia) return true;
    const fin = moment(p.fechaFinVigencia, 'YYYY-MM-DD').startOf('day');
    return fin.isSameOrAfter(today);
  };

  const renderHtml = (raw: string): string =>
    DOMPurify.sanitize(raw.replace(/\r\n|\r|\n/g, '<br />'));

  const formatPactoLabel = (p: Pacto): string => {
    if (!!p.activo) {
      const inicio = moment(p.fechaInicioVigencia, 'YYYY-MM-DD').format('DD/MM/YYYY');
      if (!p.fechaFinVigencia) {
        return `Desde ${ inicio }`;
      }
      const fin = moment(p.fechaFinVigencia, 'YYYY-MM-DD').format('DD/MM/YYYY');
      return `${ inicio } — ${ fin }`;
    } else {
      const inicio = moment(p.fechaInicioVigencia, 'YYYY-MM-DD').format('DD/MM/YYYY');
      if (!p.fechaFinVigencia) {
        return `Suspendida desde ${ inicio }`;
      }
      const fin = moment(p.fechaFinVigencia, 'YYYY-MM-DD').format('DD/MM/YYYY');
      return `Suspendida desde ${ inicio } hasta ${ fin }`;
    }
  };

  const formatCoverageName = (c: { nivelUbicacion: string; nombre: string }): string =>
    c.nivelUbicacion === 'PROVINCIA' && c.nombre === 'Buenos Aires'
      ? 'Provincia de Buenos Aires'
      : c.nombre;

  useEffect(() => {
    setIsLoading(true);

    getInsurers(false)
      .then(insurers => {
        setInsurers(insurers);
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    setPactos([]);
    setSelectedPacto(-1);
    setAranceles(undefined);
    setErrorMessage('');

    if (selectedInsurer === -1) {
      return;
    }

    setIsLoading(true);

    getPactos(selectedInsurer)
      .then(list => {
        setPactos(list);
        if (list.length === 0) {
          setErrorMessage('No se encontraron pactos para esta obra social.');
          return;
        }
        const vigente = list.find(isVigente);
        setSelectedPacto(vigente ? vigente.id : list[0].id);
      })
      .catch(err => {
        setErrorMessage(err instanceof Error ? err.message : 'No se pudieron recuperar los pactos.');
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedInsurer]);

  useEffect(() => {
    if (selectedInsurer === -1 || selectedPacto === -1) {
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setAranceles(undefined);

    getAranceles(selectedInsurer, selectedPacto)
      .then(aranceles => {
        setAranceles(aranceles);
      })
      .catch(err => {
        setAranceles(undefined);
        setErrorMessage(err instanceof Error
          ? err.message
          : 'No se pudo obtener información sobre los aranceles para la obra social.');
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedInsurer, selectedPacto]);

  useEffect(() => {
    if (! isLoading) {
      selectRef.current?.focus();
    }
  }, [isLoading]);

  return (
    <div className="content">
      <Row>
        <Col md={12}>
          <Card>
            <CardHeader>
              <Row>
                <Col style={{ fontSize: '1.5rem', marginTop: '10px' }}>Aranceles</Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Form>
                <Label for="insurer-select">Elige la Obra Social</Label>
                <InputGroup>
                  <Input
                    id="insurer-select"
                    type="select"
                    innerRef={ selectRef }
                    value={ selectedInsurer }
                    onChange={ e => setSelectedInsurer(parseInt(e.target.value)) }
                    disabled={ isLoading }
                  >
                    <option value={-1}>Selecciona una Obra Social</option>
                    {
                      insurers.map(insurer => (
                        <option key={ insurer.id } value={ insurer.id }>
                          { insurer.codigo ? `${ insurer.codigo } - ${ insurer.nombre }` : insurer.nombre }
                        </option>
                      ))
                    }
                  </Input>
                </InputGroup>
                {
                  selectedInsurer !== -1 && pactos.length > 0 && (
                    <>
                      <Label for="pacto-select" style={{ marginTop: '1rem' }}>Período</Label>
                      <InputGroup>
                        <Input
                          id="pacto-select"
                          type="select"
                          value={ selectedPacto }
                          onChange={ e => setSelectedPacto(parseInt(e.target.value)) }
                          disabled={ isLoading }
                        >
                          {
                            pactos.map(p => (
                              <option key={ p.id } value={ p.id }>{ formatPactoLabel(p) }</option>
                            ))
                          }
                        </Input>
                      </InputGroup>
                    </>
                  )
                }
              </Form>
              {
                !isLoading && selectedInsurer !== -1 && !aranceles && (
                  <Row>
                    <Col md={12}>
                      <p>
                        { errorMessage ||
                          'No se pudo obtener información sobre los aranceles para la obra social.' }
                      </p>
                    </Col>
                  </Row>
                )
              }
              {
                selectedInsurer !== -1 && aranceles && (
                  <Row>
                    <Col md={12}>
                      {
                        (() => {
                          const ins = insurers.find(i => i.id === selectedInsurer);
                          if (!ins) return null;
                          const label = ins.codigo ? `${ ins.codigo } - ${ ins.nombre }` : ins.nombre;
                          return <h5 style={{ marginBottom: '1rem' }}>{ label }</h5>;
                        })()
                      }
                      {
                        !!aranceles.activo && (
                          <>
                            <h6>Módulos</h6>
                            <Table>
                              <thead>
                                <tr>
                                  <th>Práctica</th>
                                  <th style={{ textAlign: 'right' }}>Básica</th>
                                  <th style={{ textAlign: 'right' }}>Cat A</th>
                                  <th style={{ textAlign: 'right' }}>Cat B</th>
                                  <th style={{ textAlign: 'right' }}>Cat C</th>
                                </tr>
                              </thead>
                              <tbody>
                                {
                                  aranceles.modules.map(module => (
                                    <tr key={ module.descripcion }>
                                      <td>{ module.descripcion }</td>
                                      <td style={{ textAlign: 'right' }}>${ module.basico }</td>
                                      <td style={{ textAlign: 'right' }}>${ module.catA }</td>
                                      <td style={{ textAlign: 'right' }}>${ module.catB }</td>
                                      <td style={{ textAlign: 'right' }}>${ module.catC }</td>
                                    </tr>
                                  ))
                                }
                              </tbody>
                            </Table>
                          </>
                        )
                      }
                      {
                        !!aranceles.activo && aranceles.additionals && aranceles.additionals.length > 0 && (
                          <>
                            <h6 style={{ marginTop: '2rem' }}>Adicionales</h6>
                            <Table>
                              <thead>
                                <tr>
                                  <th>Práctica</th>
                                  <th style={{ textAlign: 'right' }}>Importe</th>
                                </tr>
                              </thead>
                              <tbody>
                                {
                                  aranceles.additionals.map(additional => (
                                    <tr key={ `${ additional.descripcion }-${ additional.nombre || '' }` }>
                                      <td>
                                        { additional.descripcion }
                                        { additional.nombre ? ` (${ additional.nombre })` : '' }
                                      </td>
                                      <td style={{ textAlign: 'right' }}>${ additional.monto }</td>
                                    </tr>
                                  ))
                                }
                              </tbody>
                            </Table>
                          </>
                        )
                      }
                      {
                        aranceles.coverage && aranceles.coverage.length > 0 && (
                          <>
                            <h6 style={{ marginTop: '2rem' }}>Cobertura</h6>
                            <ul>
                              {
                                aranceles.coverage.map(c => (
                                  <li key={ `${ c.nivelUbicacion }-${ c.nombre }` }>{ formatCoverageName(c) }</li>
                                ))
                              }
                            </ul>
                          </>
                        )
                      }
                      {
                        !!aranceles.activo && aranceles.coinsurance && (
                          <>
                            <h6 style={{ marginTop: '2rem' }}>Coseguro</h6>
                            <p dangerouslySetInnerHTML={{ __html: renderHtml(aranceles.coinsurance) }} />
                          </>
                        )
                      }
                      {
                        !!aranceles.activo && (
                          <>
                            <h6 style={{ marginTop: '2rem' }}>Discapacidad</h6>
                            {
                              aranceles.impairment && aranceles.impairment.length
                                ? aranceles.impairment.length === 1
                                  ? <p>Con cobertura: ${ aranceles.impairment[0].valor }</p>
                                  : <>
                                      <p>Con cobertura:</p>
                                      <ul>
                                        {
                                          aranceles.impairment.map(i => (
                                            <li key={ `${ i.valor }-${ i.inicioVigencia ?? 'unica' }` }>
                                              ${ i.valor }
                                              {
                                                i.inicioVigencia
                                                  ? ` — desde ${ moment(i.inicioVigencia, 'YYYY-MM-DD')
                                                    .format('DD/MM/YYYY') }`
                                                  : ''
                                              }
                                            </li>
                                          ))
                                        }
                                      </ul>
                                    </>
                                : <p>Sin cobertura</p>
                            }
                          </>
                        )
                      }
                      {
                        !!aranceles.activo && aranceles.observations && (
                          <>
                            <h6 style={{ marginTop: '2rem' }}>Observaciones</h6>
                            <p dangerouslySetInnerHTML={{ __html: renderHtml(aranceles.observations) }} />
                          </>
                        )
                      }
                    </Col>
                  </Row>
                )
              }
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
